import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL, FOUNDER_EMAILS } from '../config';
import ImageCarousel from '../components/ImageCarousel';
import { FaHeart, FaRecycle } from 'react-icons/fa';

const Gallery = () => {
  const { getAccessTokenSilently, isAuthenticated, user, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [page, setPage] = useState(1);
  const [favorites, setFavorites] = useState(new Set());
  const imagesPerPage = 48;

  // Fetch all founder liked images
  useEffect(() => {
    const fetchFounderImages = async () => {
      try {
        setIsLoading(true);
        
        // Fetch images from all founders
        const responses = await Promise.all(
          FOUNDER_EMAILS.map(async email => {
            try {
              const response = await fetch(`${API_URL}/api/favorites/public/founder?founder=${encodeURIComponent(email)}`);
              if (!response.ok) {
                return [];
              }
              const images = await response.json();
              return images;
            } catch (error) {
              console.error(`Error fetching images for founder ${email}:`, error);
              return [];
            }
          })
        );

        // Combine and filter valid images
        const allImages = responses.flat();
        const validImages = allImages
          .filter(img => img && (img.url || img.s3Url || img.thumbnailUrl))
          .map(img => ({
            ...img,
            displayUrl: img.thumbnailUrl || img.s3Url || img.url
          }));

        // Remove duplicates by image id
        const uniqueImages = Array.from(
          new Map(validImages.map(img => [img.id, img])).values()
        );

        // Sort by creation date (newest first)
        uniqueImages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setImages(uniqueImages);
      } catch (error) {
        console.error('Error fetching founder images:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFounderImages();
  }, []);

  // Update favorites set when images change (if user is authenticated)
  useEffect(() => {
    if (isAuthenticated && images.length > 0) {
      const newFavorites = new Set(
        images
          .filter(img => img.isFavorited)
          .map(img => img.id)
      );
      setFavorites(newFavorites);
    }
  }, [images, isAuthenticated]);

  // Paginate images
  const paginatedImages = useMemo(() => {
    const startIndex = 0;
    const endIndex = page * imagesPerPage;
    return images.slice(startIndex, endIndex);
  }, [images, page]);

  const hasMore = paginatedImages.length < images.length;

  const handleImageClick = useCallback((image) => {
    const index = paginatedImages.findIndex(img => img.id === image.id);
    if (index !== -1) {
      setSelectedImageIndex(index);
    }
  }, [paginatedImages]);

  const handleToggleFavorite = async (imageId) => {
    if (!isAuthenticated || authLoading) return;

    try {
      const isFavorited = favorites.has(imageId);
      const token = await getAccessTokenSilently();
      const method = isFavorited ? 'DELETE' : 'POST';
      
      const response = await fetch(`${API_URL}/api/favorites/${imageId}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to update favorite');
      }

      // Update favorites set
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFavorited) {
          next.delete(imageId);
        } else {
          next.add(imageId);
        }
        return next;
      });

      // Update the image in the list
      setImages(prev => prev.map(img => 
        img.id === imageId 
          ? { ...img, isFavorited: !isFavorited }
          : img
      ));
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  // Handle recreation from gallery - redirect to create page
  const handleRegenerate = useCallback((basePrompt, spiceIds, numImages, sourceImage) => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `/gallery` }
      });
      return;
    }

    // Get the image ID from the current selection
    const currentImage = paginatedImages[selectedImageIndex];
    if (currentImage) {
      // Close the carousel and navigate to create page with recreate params
      setSelectedImageIndex(null);
      navigate(`/create?recreate=${currentImage.id}&count=${numImages}`);
    }
  }, [isAuthenticated, loginWithRedirect, selectedImageIndex, paginatedImages, navigate]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[42px] font-['VT323'] text-black/90 mb-2">
          Gallery
        </h1>
        <p className="text-[18px] font-['VT323'] text-black/60">
          Recent works from Blatant Space
        </p>
        {images.length > 0 && (
          <p className="text-[16px] font-['VT323'] text-black/40 mt-1">
            {images.length} image{images.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20">
          <motion.div 
            className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-[18px] font-['VT323'] text-black/40 mt-4">Loading gallery...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && images.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-black/40">
          <p className="text-[24px] font-['VT323'] mb-2">No images found</p>
          <p className="text-[18px] font-['VT323']">Check back later for curated images</p>
        </div>
      )}

      {/* Image Grid */}
      {!isLoading && images.length > 0 && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {paginatedImages.map((image) => (
              <motion.div
                key={image.id}
                className="relative group aspect-square rounded-lg overflow-hidden bg-black/5"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                {/* Clickable image for carousel */}
                <div 
                  className="w-full h-full cursor-pointer"
                  onClick={() => handleImageClick(image)}
                >
                  <img
                    src={image.displayUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {/* Like indicator */}
                  {isAuthenticated && favorites.has(image.id) && (
                    <div className="absolute top-2 left-2">
                      <FaHeart className="w-4 h-4 text-red-500" />
                    </div>
                  )}
                </div>

                {/* Recreate button - appears on hover */}
                <button
                  className="absolute bottom-2 right-2 p-2 rounded-full bg-[#00FF9F]/80 hover:bg-[#00FF9F] text-black opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isAuthenticated) {
                      loginWithRedirect({ appState: { returnTo: '/gallery' } });
                      return;
                    }
                    navigate(`/create?recreate=${image.id}&count=1`);
                  }}
                  title="Recreate this image"
                >
                  <FaRecycle className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <motion.button
                onClick={handleLoadMore}
                className="px-8 py-3 bg-black/[0.03] hover:bg-black/[0.05] rounded-xl font-['VT323'] text-[20px] text-black/60 hover:text-black/80 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Load More ({images.length - paginatedImages.length} remaining)
              </motion.button>
            </div>
          )}
        </div>
      )}

      {/* Image Carousel */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <ImageCarousel
            images={paginatedImages}
            currentIndex={selectedImageIndex}
            onClose={() => setSelectedImageIndex(null)}
            onToggleFavorite={isAuthenticated ? handleToggleFavorite : undefined}
            favorites={favorites}
            userId={user?.sub}
            hidePrompt={true}
            onRegenerate={handleRegenerate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
